/**
 * CountryCoverageBanner — V1 Day 7 distribution surface.
 *
 * Fetches GET /api/public/country_coverage (no auth) and renders a single
 * line:
 *   "🌍 Live in {N} countries · Brazil shipped this week · Next: {next}, {ETA}"
 *
 * Graceful failure: if the endpoint errors or returns 0 live, the banner
 * renders nothing rather than breaking the page. Auto-refreshes every 24h
 * for long-lived sessions.
 */
import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 24 * 60 * 60 * 1000; // 24h

function formatEta(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    // "Mon Jun 16, 04:00 UTC"
    const wd = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
    const md = d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
    const hm = d.toLocaleTimeString("en-US", {
      hour: "2-digit", minute: "2-digit", timeZone: "UTC", hour12: false,
    });
    return `${wd} ${md}, ${hm} UTC`;
  } catch {
    return null;
  }
}

export default function CountryCoverageBanner() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/public/country_coverage`, {
          headers: { Accept: "application/json" },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (!cancelled) setData(json);
      } catch {
        // Silent fail — graceful degradation. Banner just won't render.
        if (!cancelled) setHidden(true);
      }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (hidden || !data || !data.live_countries) return null;

  const etaFormatted = formatEta(data.next_country_eta_iso);
  const nextPart = data.next_country && etaFormatted
    ? ` · Next: ${data.next_country}, ${etaFormatted}`
    : "";

  return (
    <div
      data-testid="country-coverage-banner"
      className="w-full bg-gradient-to-r from-[#0F0F12] via-[#171623] to-[#0F0F12]
                 border-b border-[#2A2934] text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 text-center
                      text-xs sm:text-sm font-mono-data tracking-wide
                      flex items-center justify-center gap-2 flex-wrap">
        <span aria-hidden="true">🌍</span>
        <span data-testid="banner-live-count" className="text-[#A7F3D0]">
          Live in {data.live_countries} {data.live_countries === 1 ? "country" : "countries"}
        </span>
        <span className="text-[#5A575B]">·</span>
        <span className="text-[#E5E7EB]">Brazil shipped this week</span>
        {nextPart && (
          <>
            <span className="text-[#5A575B]">·</span>
            <span data-testid="banner-next-country" className="text-[#FCD34D]">
              Next: {data.next_country}, {etaFormatted}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
